import {Button, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {
    addNoteToLegalCaseAction,
    deleteLegalCaseAction,
    getLegalCasesAction,
    updateLegalCaseStatusAction
} from "../../api/legalCase";
import React, {useEffect, useState} from "react";
import {
    getPresentableLegalCaseStatus,
    getPresentableLegalCaseStatusDetail,
    LegalCaseDTO,
    LegalCaseStatus,
    LegalCaseStatusDetail,
    UserRole
} from "../../api/dtos";
import FormControl from "@mui/material/FormControl";
import CustomDialog from "../../components/CustomDialog";
import {AppState} from "../../store";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {AsyncTaskStatusType, FieldErrors} from "../../shared/dtos";
import ErrorText from "../../components/ErrorText";
import {setUpdateLegalCaseStatusTaskStatus} from "../../features/legal-case/legalCaseSlice";
import TextField from "@mui/material/TextField";

export interface AddNoteLegalCasePopupProps {
    legalCase: LegalCaseDTO;
    onClose: () => void;
    onAddNoteLegalCase: () => void;
}

export const AddNoteLegalCasePopup: React.FC<AddNoteLegalCasePopupProps> = ({
                                                                                legalCase,
                                                                                onClose,
                                                                                onAddNoteLegalCase
                                                                            }) => {
    const dispatch: any = useDispatch();
    const [note, setNote] = useState<string>('');
    const [errors, setErrors] = useState<FieldErrors>({});
    const addNoteToLegalCaseTaskStatus = useSelector((state: AppState) => state.legalCase.addNoteToLegalCaseTaskStatus);

    const handleNoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNote(event.target.value);
    };

    const handleSubmit = async () => {
        const result = await dispatch(addNoteToLegalCaseAction({id: legalCase.id, dto: {note}}));
        if (result.type === "legalCase/addNoteToLegalCaseAction/fulfilled") {
            onAddNoteLegalCase();
        }
    };

    useEffect(() => {
        if (addNoteToLegalCaseTaskStatus?.fieldErrors) {
            const updatedErrors: FieldErrors = {};
            addNoteToLegalCaseTaskStatus.fieldErrors.forEach((it) =>
                updatedErrors[it.field] = it.message
            );
            setErrors(updatedErrors);
        }
    }, [addNoteToLegalCaseTaskStatus]);

    return (
        <CustomDialog open={true} onClose={onClose}>
            <DialogTitle>Yeni Not ({legalCase.name})</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{mb: 1}}>
                    <TextField
                        required
                        margin="dense"
                        id="note"
                        name="note"
                        label="Not"
                        type="text"
                        multiline
                        rows={4}
                        fullWidth
                        variant="outlined"
                        value={note}
                        onChange={handleNoteChange}
                        inputProps={{ maxLength: 800 }}
                    />
                    {errors.note && <ErrorText message={errors.note}/>}
                    <div style={{ textAlign: "right", fontSize: 12, color: "#888" }}>
                        {note.length} / 800 karakter
                    </div>
                </FormControl>
                {addNoteToLegalCaseTaskStatus?.type === AsyncTaskStatusType.Error && addNoteToLegalCaseTaskStatus.errorMessage && (
                    <ErrorText type="form" message={addNoteToLegalCaseTaskStatus.errorMessage}/>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>İptal</Button>
                <Button onClick={handleSubmit}>Kaydet</Button>
            </DialogActions>
        </CustomDialog>
    );
};

export interface DeleteLegalCasePopupProps {
    legalCase: LegalCaseDTO;
    onClose: () => void;
    onDeleteLegalCase: () => void;
    page: number;
}

export const DeleteLegalCasePopup: React.FC<DeleteLegalCasePopupProps> = ({
                                                                              legalCase,
                                                                              onDeleteLegalCase,
                                                                              onClose,
                                                                              page
                                                                          }) => {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state: AppState) => state.auth.userWithToken?.user);

    const handleDelete = async () => {
        try {
            const result = await dispatch(deleteLegalCaseAction(legalCase.id));
            if (result.type === "legalCase/deleteLegalCase/fulfilled") {
                onDeleteLegalCase();
                const isAdminOrManager = user && [UserRole.Admin, UserRole.Manager].includes(user.role) || false;
                if (page === 1) {
                    dispatch(getLegalCasesAction({
                        companyIdFilter: null,
                        clientIdFilter: null,
                        nameFilter: null,
                        typeFilter: legalCase.type,
                        courtIdFilter: null,
                        statusFilter: null,
                        courtHouseFilter: null,
                        fileNumberFilter: null,
                        responsibleIdFilter: isAdminOrManager
                            ? null
                            : user?.id,
                        page: 1
                    }));
                } else {
                    navigate("");
                }
            }
        } catch (error) {
            console.error("Form gönderiminde bir hata oluştu:", error);
        }
    };
    return (
        <CustomDialog open={true} onClose={onClose}>
            <DialogTitle>Dava Sil ({legalCase.name})</DialogTitle>
            <DialogContent>
                Seçilen Davayı Silmek İstiyor musunuz?
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>İptal</Button>
                <Button color="error" onClick={handleDelete}>Sil</Button>
            </DialogActions>
        </CustomDialog>
    )
};

interface ChangeStatusLegalCasePopupProps {
    legalCase: LegalCaseDTO;
    onClose: () => void;
    onChangeStatus: (legalCaseStatus: LegalCaseStatus, legalCaseStatusDetail: LegalCaseStatusDetail) => void;
}

export const ChangeStatusLegalCasePopup: React.FC<ChangeStatusLegalCasePopupProps> = ({
                                                                                          legalCase,
                                                                                          onClose,
                                                                                          onChangeStatus
                                                                                      }) => {
    const statusDetailsMapping: { [key in LegalCaseStatus]: LegalCaseStatusDetail[] } = {
        [LegalCaseStatus.Acik]: [LegalCaseStatusDetail.Derdest, LegalCaseStatusDetail.KararaCikti],
        [LegalCaseStatus.Kapali]: [LegalCaseStatusDetail.Arsiv, LegalCaseStatusDetail.Infaz, LegalCaseStatusDetail.Takipsizlik],
        [LegalCaseStatus.UstMahkeme]: [LegalCaseStatusDetail.Yargitay, LegalCaseStatusDetail.Danistay, LegalCaseStatusDetail.Istinaf],
    };

    const [formData, setFormData] = useState<{
        newStatus: LegalCaseStatus,
        newStatusDetail: LegalCaseStatusDetail | null
    }>({
        newStatus: legalCase.status,
        newStatusDetail: legalCase.statusDetail
    });

    const [errors, setErrors] = useState<FieldErrors>({});
    const updateLegalCaseStatusTaskStatus = useSelector((state: AppState) => state.legalCase.updateLegalCaseStatusTaskStatus);
    const dispatch: any = useDispatch();

    useEffect(() => {
        const validDetails = statusDetailsMapping[formData.newStatus];
        if (!formData.newStatusDetail || !validDetails.includes(formData.newStatusDetail)) {
            setFormData({...formData, newStatusDetail: validDetails[0]});
        }
    }, [formData.newStatus]);

    useEffect(() => {
        return () => {
            dispatch(setUpdateLegalCaseStatusTaskStatus(null));
        };
    }, [dispatch]);

    useEffect(() => {
        if (updateLegalCaseStatusTaskStatus?.fieldErrors) {
            const updatedErrors: FieldErrors = {};
            updateLegalCaseStatusTaskStatus.fieldErrors.forEach((it) =>
                updatedErrors[it.field] = it.message
            );
            setErrors(updatedErrors);
        }
    }, [updateLegalCaseStatusTaskStatus]);

    const handleSaveClick = async () => {
        if (formData.newStatus !== legalCase.status || formData.newStatusDetail !== legalCase.statusDetail) {
            try {
                const result = await dispatch(updateLegalCaseStatusAction({
                    legalCaseId: legalCase.id,
                    newStatus: formData.newStatus,
                    newDetail: formData.newStatusDetail || ""
                }));
                if (result.type === "legalCase/updateLegalCaseStatus/fulfilled") {
                    onChangeStatus(formData.newStatus, formData.newStatusDetail!);
                }
            } catch (error) {
                console.error("Güncelleme işlemi başarısız oldu: ", error);
            }
        }
    };

    const handleChangeFormData = (event: any) => {
        const {name, value} = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const hasChanges = JSON.stringify(formData) !== JSON.stringify({
        newStatus: legalCase.status,
        newStatusDetail: legalCase.statusDetail
    });

    return (
        <CustomDialog open={true} onClose={onClose}>
            <DialogTitle>Durumu Değiştir</DialogTitle>
            <DialogContent>
                <FormControl fullWidth={true} sx={{mt: 2, mb: 1}}>
                    <InputLabel id="new-status-label">Durum Seç</InputLabel>
                    <Select
                        labelId="new-status-label"
                        id="newStatus"
                        name="newStatus"
                        label="Durum Seç"
                        fullWidth
                        value={formData.newStatus}
                        onChange={handleChangeFormData}>
                        {Object.values(LegalCaseStatus).map((statusValue) => (
                            <MenuItem key={statusValue} value={statusValue}>
                                {getPresentableLegalCaseStatus(statusValue)}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.newStatus && <ErrorText message={errors.newStatus}/>}
                </FormControl>
                <FormControl fullWidth={true} sx={{mt: 2, mb: 1}}>
                    <InputLabel id="new-case-status-label">Detay Seç</InputLabel>
                    <Select
                        labelId="new-case-status-label"
                        id="newCaseStatus"
                        name="newStatusDetail"
                        label="Detay Seç"
                        fullWidth
                        value={formData.newStatusDetail}
                        onChange={handleChangeFormData}>
                        {statusDetailsMapping[formData.newStatus].map((statusDetailValue) => (
                            <MenuItem key={statusDetailValue} value={statusDetailValue}>
                                {getPresentableLegalCaseStatusDetail(statusDetailValue)}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.newStatusDetail && <ErrorText message={errors.newStatusDetail}/>}
                </FormControl>
                {updateLegalCaseStatusTaskStatus?.type === AsyncTaskStatusType.Error && updateLegalCaseStatusTaskStatus.errorMessage && (
                    <ErrorText type="form" message={updateLegalCaseStatusTaskStatus.errorMessage}/>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>İptal</Button>
                <Button onClick={handleSaveClick} color="primary" disabled={!hasChanges}>
                    Kaydet
                </Button>
            </DialogActions>
        </CustomDialog>
    );
};
